/**
 * Grid Canvas Component
 * 
 * Handles rendering of the 21x21 city grid with hexagonal-style positioning.
 * Ported from Flash Canvas layers (background_layer, resource_layer, lock_layer, main_canvas)
 */

import { GameEngine } from '../core/GameEngine.js';
import { WALL, DEEP_WATER, CELLARIUM, CACHE, BARRACKS, MANOR, WHARF, BAZAAR, FARM, MILL, WOOD, OPEN, WATER, FARM_OLD, TOWN_HALL } from '../core/Constants.js';
import { isRealResource, isResource, isMilitary, isBuilding } from '../core/Constants.js';
import { getTileImageKey, shouldRenderTile } from '../utils/TileImageMap.js';

export class GridCanvas {
    constructor(canvasElement, gameEngine) {
        // Support both single canvas (legacy) and multi-layer canvas system
        // If canvasElement is a container, find the layer canvases
        if (canvasElement && canvasElement.classList && canvasElement.classList.contains('canvas-layers')) {
            // Multi-layer system
            this.canvasContainer = canvasElement;
            // Query within the container first, then fall back to getElementById
            // Use querySelector with just the ID (no # needed when querying by ID attribute)
            this.canvasBg = canvasElement.querySelector('#city-grid-bg') || document.getElementById('city-grid-bg');
            this.canvasLines = canvasElement.querySelector('#city-grid-lines') || document.getElementById('city-grid-lines');
            this.canvasOverlay = canvasElement.querySelector('#city-grid-overlay') || document.getElementById('city-grid-overlay');
            
            console.log('Canvas element lookup:', {
                container: canvasElement,
                foundBg: !!this.canvasBg,
                foundLines: !!this.canvasLines,
                foundOverlay: !!this.canvasOverlay,
                containerChildren: canvasElement.children.length,
                firstChild: canvasElement.firstElementChild?.tagName
            });
            
            // Check if all canvas elements were found
            if (!this.canvasBg || !this.canvasLines || !this.canvasOverlay) {
                console.error('Multi-layer canvas elements not found. Found:', {
                    bg: !!this.canvasBg,
                    lines: !!this.canvasLines,
                    overlay: !!this.canvasOverlay,
                    container: canvasElement,
                    containerHTML: canvasElement ? canvasElement.innerHTML.substring(0, 200) : 'null'
                });
                // Fallback: try to find a single canvas or use the first child
                const firstCanvas = canvasElement.querySelector('canvas');
                if (firstCanvas) {
                    console.warn('Falling back to single canvas mode');
                    this.canvas = firstCanvas;
                    this.ctx = firstCanvas.getContext('2d');
                    this.canvasBg = this.canvas;
                    this.canvasLines = this.canvas;
                    this.canvasOverlay = this.canvas;
                    this.ctxBg = this.ctx;
                    this.ctxLines = this.ctx;
                    this.ctxOverlay = this.ctx;
                } else {
                    throw new Error('No canvas elements found in container');
                }
            } else {
                // Use overlay canvas as primary for event handling (topmost layer)
                this.canvas = this.canvasOverlay;
                
                // Get contexts for each layer
                this.ctxBg = this.canvasBg.getContext('2d');
                this.ctxLines = this.canvasLines.getContext('2d');
                this.ctxOverlay = this.canvasOverlay.getContext('2d');
                
                // Primary context for compatibility (overlay layer)
                this.ctx = this.ctxOverlay;
            }
        } else {
            // Single canvas (legacy support)
            this.canvas = canvasElement;
            this.ctx = canvasElement.getContext('2d');
            this.canvasBg = this.canvas;
            this.canvasLines = this.canvas;
            this.canvasOverlay = this.canvas;
            this.ctxBg = this.ctx;
            this.ctxLines = this.ctx;
            this.ctxOverlay = this.ctx;
        }
        
        this.gameEngine = gameEngine;
        
        // Grid dimensions
        this.gridWidth = 21;
        this.gridHeight = 21;
        this.tileWidth = 38;  // Match working version: 38px width
        this.tileHeight = 27; // Match working version: 27px height
        this.tileSpacingX = 38;
        this.tileSpacingY = 27; // Match tile height for regular grid (no overlap)
        
        // Grid positioning constants (from Flash code)
        this.gridOffsetX = 7;
        this.gridOffsetY = 11;
        
        // Canvas settings - calculate size to fit all tiles
        // Canvas must fit: offset + (gridSize * spacing) to include last tile
        this.canvasWidth = this.gridOffsetX + (this.gridWidth * this.tileSpacingX);
        this.canvasHeight = this.gridOffsetY + (this.gridHeight * this.tileSpacingY);
        
        // Set size for all canvas layers (with null checks)
        if (this.canvasBg) {
            this.canvasBg.setAttribute('width', this.canvasWidth);
            this.canvasBg.setAttribute('height', this.canvasHeight);
        }
        if (this.canvasLines) {
            this.canvasLines.setAttribute('width', this.canvasWidth);
            this.canvasLines.setAttribute('height', this.canvasHeight);
        }
        if (this.canvasOverlay) {
            this.canvasOverlay.setAttribute('width', this.canvasWidth);
            this.canvasOverlay.setAttribute('height', this.canvasHeight);
        }
        
        // Image cache
        this.imageCache = new Map();
        this.assetLoader = null; // Will be set by MainApp
        
        // Rendering flags
        this.showGridlines = true;
        this.water = false;
        this.showHeatMap = false;
        this.showBuildingLevels = true;
        
        // Background images (legacy)
        this.landBgImage = null;
        this.waterBgImage = null;
        
        // City Design - 3 Layer System images
        this.cityDesignLandImage = null;
        this.cityDesignWaterWaterImage = null;
        this.cityDesignWallWoodLImage = null;
        this.cityDesignWallWoodWImage = null;
        this.cityDesignCityWallsImage = null;
        this.cityDesignWallsWater2Image = null;
        
        // Cached wheat image for farm bonus indicators
        this.wheatImage = null;
        this.wheatImageChecked = false;
        
        // Mouse tracking
        this.hoverTile = null;
        this.selectedTile = null;
        
        // Cursor mode (1x1, 3x1, 1x3, 2x3, 3x2)
        this.cursorMode = '1x1';
        
        // Event handlers
        this.onTileClick = null;
        this.onTileHover = null;
        
        // Setup mouse handlers
        this.setupEventListeners();
        
        // Setup responsive scaling
        this.setupResponsiveScaling();
    }
    
    /**
     * Setup responsive scaling to fit canvas in container
     */
    setupResponsiveScaling() {
        // Calculate and apply initial scale
        this.updateScale();
        
        // Update scale on window resize
        window.addEventListener('resize', () => {
            this.updateScale();
        });
    }
    
    /**
     * Update canvas scale to fit container while maintaining aspect ratio
     */
    updateScale() {
        if (!this.canvas || !this.canvas.parentElement) return;
        
        const container = this.canvasContainer || this.canvas.parentElement;
        const containerWidth = container.clientWidth - 20; // Account for padding
        const containerHeight = container.clientHeight - 20;
        
        // Only scale if container is smaller than canvas
        if (containerWidth >= this.canvasWidth && containerHeight >= this.canvasHeight) {
            // Container is large enough, use original size
            const size = `${this.canvasWidth}px`;
            this.canvasBg.style.width = size;
            this.canvasBg.style.height = size;
            this.canvasLines.style.width = size;
            this.canvasLines.style.height = size;
            this.canvasOverlay.style.width = size;
            this.canvasOverlay.style.height = size;
            return;
        }
        
        // Calculate scale to fit both width and height
        const scaleX = containerWidth / this.canvasWidth;
        const scaleY = containerHeight / this.canvasHeight;
        const scale = Math.min(scaleX, scaleY); // Use the smaller scale to fit both dimensions
        
        // Apply scale via CSS to all layers
        const scaledWidth = `${this.canvasWidth * scale}px`;
        const scaledHeight = `${this.canvasHeight * scale}px`;
        this.canvasBg.style.width = scaledWidth;
        this.canvasBg.style.height = scaledHeight;
        this.canvasLines.style.width = scaledWidth;
        this.canvasLines.style.height = scaledHeight;
        this.canvasOverlay.style.width = scaledWidth;
        this.canvasOverlay.style.height = scaledHeight;
    }
    
    /**
     * Convert grid coordinates to canvas pixel coordinates
     * Regular grid layout: x = 7+j*44, y = 11+i*44
     */
    gridToPixel(i, j) {
        const x = this.gridOffsetX + (j * this.tileSpacingX);
        const y = this.gridOffsetY + (i * this.tileSpacingY);
        return { x, y };
    }
    
    /**
     * Convert canvas pixel coordinates to grid coordinates
     * Accounts for CSS scaling by converting display coordinates to canvas coordinates
     */
    pixelToGrid(x, y) {
        // Get the actual canvas dimensions (not the scaled display size)
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;
        
        // Convert display coordinates to canvas coordinates
        const canvasX = x * scaleX;
        const canvasY = y * scaleY;
        
        // Account for offset (match original Flash positioning)
        const adjustedX = canvasX - this.gridOffsetX;
        const adjustedY = canvasY - this.gridOffsetY;
        
        // Calculate grid position
        const j = Math.floor(adjustedX / this.tileSpacingX);
        const i = Math.floor(adjustedY / this.tileSpacingY);
        
        // Clamp to valid grid bounds
        if (i >= 0 && i < this.gridHeight && j >= 0 && j < this.gridWidth) {
            return { i, j };
        }
        
        return null;
    }
    
    /**
     * Setup mouse and keyboard event listeners
     */
    setupEventListeners() {
        // Mouse move for hover
        this.canvas.addEventListener('mousemove', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            const gridPos = this.pixelToGrid(x, y);
            if (gridPos && (gridPos.i !== this.hoverTile?.i || gridPos.j !== this.hoverTile?.j)) {
                this.hoverTile = gridPos;
                this.renderSync(); // Use renderSync for multi-layer rendering
                
                if (this.onTileHover) {
                    this.onTileHover(gridPos.i, gridPos.j);
                }
            }
        });
        
        // Mouse leave
        this.canvas.addEventListener('mouseleave', () => {
            this.hoverTile = null;
            this.renderSync(); // Use renderSync for multi-layer rendering
        });
        
        // Mouse click
        this.canvas.addEventListener('click', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            const gridPos = this.pixelToGrid(x, y);
            if (gridPos) {
                this.selectedTile = gridPos;
                
                if (this.onTileClick) {
                    this.onTileClick(gridPos.i, gridPos.j, e);
                }
            }
        });
    }
    
    /**
     * Load an image and cache it
     */
    async loadImage(src) {
        if (this.imageCache.has(src)) {
            return this.imageCache.get(src);
        }
        
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.imageCache.set(src, img);
                resolve(img);
            };
            img.onerror = reject;
            img.src = src;
        });
    }
    
    /**
     * Get image for a tile type
     */
    async getTileImage(tile) {
        if (!this.assetLoader) {
            return null;
        }
        
        const imageKey = getTileImageKey(tile);
        if (!imageKey) {
            return null;
        }
        
        // Get image from asset loader
        const image = await this.assetLoader.getImage(imageKey);
        return image;
    }
    
    /**
     * Set asset loader
     */
    setAssetLoader(assetLoader) {
        this.assetLoader = assetLoader;
        // Cache wheat image reference once asset loader is set
        this.cacheWheatImage();
    }
    
    /**
     * Cache wheat image reference for consistent rendering
     */
    cacheWheatImage() {
        if (this.assetLoader && this.assetLoader.images) {
            const wheatImg = this.assetLoader.images['wheat'];
            // Only cache if image exists and is fully loaded
            if (wheatImg && 
                typeof wheatImg === 'object' &&
                'complete' in wheatImg &&
                'naturalWidth' in wheatImg &&
                wheatImg.complete && 
                wheatImg.naturalWidth > 0 && 
                wheatImg.naturalHeight > 0) {
                this.wheatImage = wheatImg;
                this.wheatImageChecked = true;
            } else {
                // If image exists but not loaded yet, try again later
                if (wheatImg && typeof wheatImg === 'object' && 'complete' in wheatImg) {
                    if (wheatImg.complete) {
                        // Image loaded but invalid
                        this.wheatImage = null;
                        this.wheatImageChecked = true;
                    } else {
                        // Image not loaded yet, wait for it
                        const onloadHandler = () => {
                            if (wheatImg.naturalWidth > 0 && wheatImg.naturalHeight > 0) {
                                this.wheatImage = wheatImg;
                                this.wheatImageChecked = true;
                                // Re-render when image loads
                                this.renderSync();
                            } else {
                                this.wheatImage = null;
                                this.wheatImageChecked = true;
                            }
                            // Remove handler to prevent memory leaks
                            wheatImg.removeEventListener('load', onloadHandler);
                        };
                        wheatImg.addEventListener('load', onloadHandler);
                        // Also check if already loaded (race condition)
                        if (wheatImg.complete && wheatImg.naturalWidth > 0) {
                            onloadHandler();
                        }
                    }
                } else {
                    this.wheatImage = null;
                    this.wheatImageChecked = true;
                }
            }
        }
    }
    
    /**
     * Load background images
     */
    async loadBackgrounds() {
        if (!this.assetLoader) return;
        
        try {
            // Legacy backgrounds (kept for compatibility)
            this.landBgImage = await this.assetLoader.getImage('bg_land');
            this.waterBgImage = await this.assetLoader.getImage('bg_water');
            
            // City Design - 3 Layer System
            this.cityDesignLandImage = await this.assetLoader.getImage('city_design_land');
            this.cityDesignWaterWaterImage = await this.assetLoader.getImage('city_design_water_water');
            this.cityDesignWallWoodLImage = await this.assetLoader.getImage('city_design_wall_wood_l');
            this.cityDesignWallWoodWImage = await this.assetLoader.getImage('city_design_wall_wood_w');
            this.cityDesignCityWallsImage = await this.assetLoader.getImage('city_design_city_walls');
            this.cityDesignWallsWater2Image = await this.assetLoader.getImage('city_design_walls_water2');
        } catch (error) {
            console.warn('Failed to load background images:', error);
        }
    }
    
    /**
     * Draw a single tile
     */
    async drawTile(i, j, tileType) {
        const pos = this.gridToPixel(i, j);
        
        // Skip if outside bounds
        if (pos.x < -this.tileWidth || pos.x > this.canvasWidth ||
            pos.y < -this.tileHeight || pos.y > this.canvasHeight) {
            return;
        }
        
        // NOTE: Tile background/buildable overlay and gridlines are now drawn separately 
        // in renderSync() on their respective layers. The async render() method should not
        // be used in multi-layer mode. We don't draw anything here to avoid conflicts.
        
        // Draw tile content (building/resource image)
        if (shouldRenderTile(tileType) && this.assetLoader) {
            try {
                const imageKey = getTileImageKey(tileType);
                if (imageKey) {
                    const image = await this.assetLoader.getImage(imageKey);
                    if (image) {
                        // Scale 64x64 image to fit within tile (38x27), maintaining aspect ratio
                        const imageSize = 64;
                        const scale = Math.min(this.tileWidth / imageSize, this.tileHeight / imageSize);
                        const scaledWidth = imageSize * scale;
                        const scaledHeight = imageSize * scale;
                        const offsetX = (this.tileWidth - scaledWidth) / 2;
                        const offsetY = (this.tileHeight - scaledHeight) / 2;
                        this.ctx.drawImage(image, pos.x + offsetX, pos.y + offsetY, scaledWidth, scaledHeight);
                    }
                }
            } catch (error) {
                // Image not loaded yet, skip for now
                console.debug(`Image not loaded for tile ${tileType.toString(16)}`);
            }
        }
        
        // Draw lock indicator if locked
        if (this.gameEngine && this.gameEngine.isLocked(i, j)) {
            const lockImage = this.assetLoader?.getImage('locked');
            if (lockImage) {
                const lockImg = await lockImage;
                if (lockImg) {
                    this.ctx.drawImage(lockImg, pos.x + this.tileWidth - 16, pos.y + 6, 16, 16);
                }
            } else {
                // Fallback: red overlay
                this.ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
                this.ctx.fillRect(pos.x + this.tileWidth - 16, pos.y + 6, 16, 16);
            }
        }
        
        // Draw hover highlight
        if (this.hoverTile && this.hoverTile.i === i && this.hoverTile.j === j) {
            this.ctx.strokeStyle = '#00ff00';
            this.ctx.lineWidth = 2;
            this.ctx.strokeRect(pos.x, pos.y, this.tileWidth, this.tileHeight);
        }
        
        // Draw cursor overlay based on cursor mode
        if (this.shouldShowCursor(i, j)) {
            this.drawCursor(i, j);
        }
        
        // Draw heat map overlay
        if (this.showHeatMap) {
            this.drawHeatMap(i, j, tileType);
        }
    }
    
    /**
     * Check if cursor should be shown at this position
     */
    shouldShowCursor(i, j) {
        if (!this.hoverTile) return false;
        
        const [rows, cols] = this.cursorMode.split('x').map(Number);
        
        // Check if this tile is within the cursor area
        const baseI = this.hoverTile.i;
        const baseJ = this.hoverTile.j;
        
        const iDiff = i - baseI;
        const jDiff = j - baseJ;
        
        return iDiff >= 0 && iDiff < rows && jDiff >= 0 && jDiff < cols;
    }
    
    /**
     * Draw cursor overlay
     */
    drawCursor(i, j) {
        const pos = this.gridToPixel(i, j);
        this.ctx.strokeStyle = '#ffff00';
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([4, 4]);
        this.ctx.strokeRect(pos.x, pos.y, this.tileWidth, this.tileHeight);
        this.ctx.setLineDash([]);
    }
    
    /**
     * Get wall image based on terrain and level
     */
    getWallImage() {
        if (!this.gameEngine) return null;
        
        const wallLevel = this.gameEngine.wallLevel !== undefined && this.gameEngine.wallLevel !== null 
            ? this.gameEngine.wallLevel 
            : 10;
        
        if (this.water) {
            // Water city walls
            if (wallLevel === 0) {
                return this.cityDesignWallWoodWImage;
            } else {
                return this.cityDesignWallsWater2Image;
            }
        } else {
            // Land city walls
            if (wallLevel === 0) {
                return this.cityDesignWallWoodLImage;
            } else {
                return this.cityDesignCityWallsImage;
            }
        }
    }
    
    /**
     * Draw background layer (terrain) - 3 Layer System
     * Layer 1: Base land image (always) - covers 100% width and height
     * Layer 2: Water overlay (if water terrain) - positioned in bottom right corner
     * Layer 3: City walls (based on terrain and level) - covers 100% width and height
     */
    drawBackground(ctx = null) {
        const targetCtx = ctx || this.ctxBg;
        if (!targetCtx) {
            console.error('drawBackground: No context provided');
            return;
        }
        
        console.log('drawBackground called - water:', this.water, 'landImage:', !!this.cityDesignLandImage, 'waterImage:', !!this.cityDesignWaterWaterImage);
        
        // LAYER 1: Base layer - Always draw land.jpg covering 100% width and height
        // This matches background-size: 100% 100% (stretch to fit)
        if (this.cityDesignLandImage && this.cityDesignLandImage.complete && this.cityDesignLandImage.naturalWidth > 0) {
            // Draw land image covering the entire canvas (100% width and height)
            targetCtx.drawImage(this.cityDesignLandImage, 0, 0, this.canvasWidth, this.canvasHeight);
        } else {
            // Fallback: solid color
            targetCtx.fillStyle = '#2a4a3a';
            targetCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
        }
        
        // LAYER 2: Water overlay - If water terrain, draw water_water.png aligned with water tiles
        // Based on original CSS: width: 898px, height: 590.25px, right: 0px, bottom: 0px
        // Position at bottom-right corner, aligned with canvas edges
        const shouldDrawWater = this.water && this.cityDesignWaterWaterImage && 
            this.cityDesignWaterWaterImage.complete && this.cityDesignWaterWaterImage.naturalWidth > 0;
        console.log('Should draw water overlay:', shouldDrawWater, 'water:', this.water, 'image complete:', this.cityDesignWaterWaterImage?.complete);
        if (shouldDrawWater) {
            const imageWidth = this.cityDesignWaterWaterImage.naturalWidth;
            const imageHeight = this.cityDesignWaterWaterImage.naturalHeight;
            
            // Original dimensions from CSS: 898px x 590.25px
            // Scale proportionally based on canvas size relative to original city_map size (2688x2016)
            const originalMapWidth = 2688;
            const originalMapHeight = 2016;
            const originalWaterWidth = 898;
            const originalWaterHeight = 590.25;
            
            // Calculate scale factor based on canvas size
            const scaleX = this.canvasWidth / originalMapWidth;
            const scaleY = this.canvasHeight / originalMapHeight;
            
            // Calculate target size for water overlay (proportional to original)
            // Use the exact scaled dimensions to match CSS background-size: 100% 100%
            const scaledWidth = originalWaterWidth * scaleX;
            const scaledHeight = originalWaterHeight * scaleY;
            
            // Position at bottom-right corner (right: 0, bottom: 0)
            const x = this.canvasWidth - scaledWidth;
            const y = this.canvasHeight - scaledHeight;
            
            targetCtx.drawImage(this.cityDesignWaterWaterImage, x, y, scaledWidth, scaledHeight);
        }
        
        // LAYER 3: City walls - Draw based on terrain and level
        // Based on original CSS: width: 100%, height: 100%, background-size: 100% 100%
        const wallImage = this.getWallImage();
        if (wallImage && wallImage.complete && wallImage.naturalWidth > 0) {
            // Draw wall image covering 100% width and height (stretch to fit)
            // This matches the original CSS: background-size: 100% 100%
            targetCtx.drawImage(wallImage, 0, 0, this.canvasWidth, this.canvasHeight);
        }
    }
    
    /**
     * Render the entire grid
     */
    async render() {
        // Clear canvas
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        
        // Draw background
        this.drawBackground();
        
        // Draw all tiles (note: drawTile is async but we render synchronously for now)
        if (this.gameEngine) {
            for (let i = 0; i < this.gridHeight; i++) {
                for (let j = 0; j < this.gridWidth; j++) {
                    const tile = this.gameEngine.map[i][j];
                    
                    // Skip walls (non-buildable areas)
                    if (tile === WALL || tile === DEEP_WATER) {
                        continue;
                    }
                    
                    // Note: For better performance, we'll batch image loading
                    // For now, render synchronously
                    await this.drawTile(i, j, tile);
                }
            }
        }
    }
    
    /**
     * Render the grid (non-async version for compatibility)
     * Uses separate layers: background, gridlines, overlay
     */
    renderSync() {
        // Check if contexts are available
        if (!this.ctxBg || !this.ctxLines || !this.ctxOverlay) {
            console.error('Canvas contexts not initialized', {
                ctxBg: !!this.ctxBg,
                ctxLines: !!this.ctxLines,
                ctxOverlay: !!this.ctxOverlay,
                canvasBg: !!this.canvasBg,
                canvasLines: !!this.canvasLines,
                canvasOverlay: !!this.canvasOverlay
            });
            return;
        }
        
        // LAYER 1: Background layer (background and tiles)
        this.ctxBg.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.drawBackground(this.ctxBg);
        
        // Draw all tiles on background layer
        if (this.gameEngine) {
            for (let i = 0; i < this.gridHeight; i++) {
                for (let j = 0; j < this.gridWidth; j++) {
                    const tile = this.gameEngine.map[i][j];
                    
                    // Skip walls (non-buildable areas)
                    if (tile === WALL || tile === DEEP_WATER) {
                        continue;
                    }
                    
                    // Draw wheat fields first (even on OPEN tiles) before drawing tile content
                    // This ensures wheat fields are always drawn, even on empty tiles
                    if (this.gameEngine.hasWheatField(i, j)) {
                        const pos = this.gridToPixel(i, j);
                        this.drawWheatFieldOnContext(pos.x, pos.y, this.ctxBg);
                    }
                    
                    // Draw tile content on background layer
                    this.drawTileContentSync(i, j, tile, this.ctxBg);
                }
            }
        }
        
        // LAYER 2: Gridlines layer
        this.ctxLines.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        if (this.showGridlines && this.gameEngine) {
            this.drawGridlinesSync();
        }
        
        // LAYER 3: Overlay layer (hover effects, heatmap, etc.)
        this.ctxOverlay.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        
        // Draw heat map overlay on all tiles if enabled
        if (this.showHeatMap && this.gameEngine) {
            for (let i = 0; i < this.gridHeight; i++) {
                for (let j = 0; j < this.gridWidth; j++) {
                    const tile = this.gameEngine.map[i][j];
                    
                    // Skip walls and deep water
                    if (tile === WALL || tile === DEEP_WATER) {
                        continue;
                    }
                    
                    // Draw heat map on overlay layer
                    this.drawHeatMapOnContext(i, j, tile, this.ctxOverlay);
                }
            }
        }
        
        // Draw hover highlight on overlay layer
        if (this.hoverTile && this.gameEngine) {
            this.drawHoverHighlight();
        }
        
        // Draw building levels on overlay layer (if enabled)
        if (this.showBuildingLevels && this.gameEngine) {
            for (let i = 0; i < this.gridHeight; i++) {
                for (let j = 0; j < this.gridWidth; j++) {
                    const tile = this.gameEngine.map[i][j];
                    
                    // Skip deep water
                    if (tile === DEEP_WATER) {
                        continue;
                    }
                    
                    // Draw level for walls (only at 0,0 position), buildings (including Town Hall/Basilica)
                    // Note: TOWN_HALL (0x0006) is not included in isBuilding() because it's < 0x1000,
                    // so we check for it separately
                    if (tile === WALL) {
                        // Only draw wall level indicator at position 0,0
                        if (i === 0 && j === 0) {
                            this.drawBuildingLevel(i, j, tile);
                        }
                    } else if (tile === TOWN_HALL || isBuilding(tile)) {
                        this.drawBuildingLevel(i, j, tile);
                    }
                }
            }
        }
    }
    
    /**
     * Draw tile content without gridlines (for proper layering)
     * @param {number} i - Row position
     * @param {number} j - Column position
     * @param {number} tileType - Tile type constant
     * @param {CanvasRenderingContext2D} ctx - The context to draw on (defaults to background layer)
     */
    drawTileContentSync(i, j, tileType, ctx = null) {
        const targetCtx = ctx || this.ctxBg;
        const pos = this.gridToPixel(i, j);
        
        // Skip if outside bounds
        if (pos.x < -this.tileWidth || pos.x > this.canvasWidth ||
            pos.y < -this.tileHeight || pos.y > this.canvasHeight) {
            return;
        }
        
        // Note: Wheat fields are now drawn in the main render loop before calling drawTileContentSync
        // This ensures they're drawn even on OPEN tiles. We don't draw them here to avoid double-drawing.
        
        // Draw tile content (building/resource image) - only if already loaded
        if (shouldRenderTile(tileType) && this.assetLoader) {
            try {
                const imageKey = getTileImageKey(tileType);
                if (imageKey) {
                    const image = this.assetLoader.images[imageKey];
                    if (image && image.complete && image.naturalWidth > 0) {
                        // Scale 64x64 image to fit within tile (38x27), maintaining aspect ratio
                        const imageSize = 64;
                        const scale = Math.min(this.tileWidth / imageSize, this.tileHeight / imageSize);
                        const scaledWidth = imageSize * scale;
                        const scaledHeight = imageSize * scale;
                        const offsetX = (this.tileWidth - scaledWidth) / 2;
                        const offsetY = (this.tileHeight - scaledHeight) / 2;
                        targetCtx.drawImage(image, pos.x + offsetX, pos.y + offsetY, scaledWidth, scaledHeight);
                    }
                }
            } catch (error) {
                // Image not loaded
            }
        }
        
        // Draw lock indicator if locked
        if (this.gameEngine && this.gameEngine.isLocked(i, j)) {
            const lockImage = this.assetLoader?.images['locked'];
            if (lockImage && lockImage.complete) {
                targetCtx.drawImage(lockImage, pos.x + this.tileWidth - 16, pos.y + 6, 16, 16);
            }
        }
    }
    
    /**
     * Draw gridlines in a separate pass (on gridlines layer)
     */
    drawGridlinesSync() {
        if (!this.gameEngine) return;
        
        this.ctxLines.strokeStyle = '#000000';
        this.ctxLines.lineWidth = 1;
        
        for (let i = 0; i < this.gridHeight; i++) {
            for (let j = 0; j < this.gridWidth; j++) {
                const tile = this.gameEngine.map[i][j];
                
                // Skip walls and deep water
                if (tile === WALL || tile === DEEP_WATER) {
                    continue;
                }
                
                const pos = this.gridToPixel(i, j);
                
                // Skip if outside bounds
                if (pos.x < -this.tileWidth || pos.x > this.canvasWidth ||
                    pos.y < -this.tileHeight || pos.y > this.canvasHeight) {
                    continue;
                }
                
                // Draw gridline on gridlines layer
                this.ctxLines.strokeRect(pos.x, pos.y, this.tileWidth, this.tileHeight);
            }
        }
    }
    
    /**
     * Draw a single wheat field at the specified pixel position on a specific context
     * @param {number} x - X pixel position
     * @param {number} y - Y pixel position
     * @param {CanvasRenderingContext2D} ctx - Context to draw on
     */
    drawWheatFieldOnContext(x, y, ctx) {
        // Ensure wheat image is cached
        if (!this.wheatImage && this.assetLoader && !this.wheatImageChecked) {
            this.cacheWheatImage();
        }
        
        // Draw wheat if we have the image and it's fully loaded
        if (this.wheatImage && ctx && this.wheatImage.complete && this.wheatImage.naturalWidth > 0) {
            try {
                const imageSize = 64;
                const scale = Math.min(this.tileWidth / imageSize, this.tileHeight / imageSize) * 0.7; // Slightly smaller
                const scaledWidth = imageSize * scale;
                const scaledHeight = imageSize * scale;
                const offsetX = (this.tileWidth - scaledWidth) / 2;
                const offsetY = (this.tileHeight - scaledHeight) / 2;
                
                // Save context state to preserve any existing transformations
                ctx.save();
                ctx.drawImage(this.wheatImage, x + offsetX, y + offsetY, scaledWidth, scaledHeight);
                ctx.restore();
            } catch (error) {
                // Image not loaded or error drawing
                console.warn(`Error drawing wheat field at (${x}, ${y}):`, error);
            }
        }
    }
    
    /**
     * Draw a single wheat field at the specified pixel position (uses default context)
     * @param {number} x - X pixel position
     * @param {number} y - Y pixel position
     */
    drawWheatField(x, y) {
        this.drawWheatFieldOnContext(x, y, this.ctx);
    }
    
    /**
     * Draw hover highlight on overlay layer
     */
    drawHoverHighlight() {
        if (!this.hoverTile || !this.ctxOverlay) return;
        
        const pos = this.gridToPixel(this.hoverTile.i, this.hoverTile.j);
        
        // Skip if outside bounds
        if (pos.x < -this.tileWidth || pos.x > this.canvasWidth ||
            pos.y < -this.tileHeight || pos.y > this.canvasHeight) {
            return;
        }
        
        // Draw green highlight rectangle
        this.ctxOverlay.strokeStyle = '#00ff00';
        this.ctxOverlay.lineWidth = 2;
        this.ctxOverlay.strokeRect(pos.x, pos.y, this.tileWidth, this.tileHeight);
    }
    
    /**
     * Draw building level on overlay layer
     * @param {number} i - Row position
     * @param {number} j - Column position
     * @param {number} tile - Tile type constant
     */
    drawBuildingLevel(i, j, tile) {
        if (!this.ctxOverlay || !this.gameEngine) return;
        
        const pos = this.gridToPixel(i, j);
        
        // Skip if outside bounds
        if (pos.x < -this.tileWidth || pos.x > this.canvasWidth ||
            pos.y < -this.tileHeight || pos.y > this.canvasHeight) {
            return;
        }
        
        // Get building level
        let level;
        let isBasilica = false;
        let isWall = false;
        if (tile === TOWN_HALL) {
            isBasilica = true;
            level = this.gameEngine.basilicaLevel !== undefined && this.gameEngine.basilicaLevel !== null 
                ? this.gameEngine.basilicaLevel 
                : 10;
        } else if (tile === WALL) {
            isWall = true;
            level = this.gameEngine.wallLevel !== undefined && this.gameEngine.wallLevel !== null 
                ? this.gameEngine.wallLevel 
                : 10;
        } else {
            level = this.gameEngine.buildingLevel !== undefined && this.gameEngine.buildingLevel !== null 
                ? this.gameEngine.buildingLevel 
                : 10;
        }
        
        const fontSize = 10;
        const padding = 2;
        let textX, textY;
        let textAlign, textBaseline;
        
        // Walls: draw in top-left corner (0,0 position)
        if (isWall && i === 0 && j === 0) {
            textX = pos.x + padding;
            textY = pos.y + padding;
            textAlign = 'left';
            textBaseline = 'top';
        } else {
            // All other buildings: draw in bottom-right corner
            textX = pos.x + this.tileWidth - padding;
            textY = pos.y + this.tileHeight - padding;
            textAlign = 'right';
            textBaseline = 'bottom';
        }
        
        // Draw background for better visibility
        this.ctxOverlay.save();
        
        // Use different styling for Basilica (Town Hall) vs regular buildings vs walls
        if (isBasilica) {
            // Basilica: Gold/yellow background with darker text
            this.ctxOverlay.fillStyle = 'rgba(199, 168, 107, 0.9)'; // Bronze/gold color
            this.ctxOverlay.font = `bold ${fontSize}px Arial, sans-serif`;
            this.ctxOverlay.textAlign = textAlign;
            this.ctxOverlay.textBaseline = textBaseline;
            
            // Measure text to create background
            const metrics = this.ctxOverlay.measureText(level.toString());
            const textWidth = metrics.width;
            const textHeight = fontSize;
            const bgPadding = 2;
            
            // Draw background
            let bgX, bgY;
            if (isWall && i === 0 && j === 0) {
                bgX = textX - bgPadding;
                bgY = textY - bgPadding;
            } else {
                bgX = textX - textWidth - bgPadding;
                bgY = textY - textHeight - bgPadding;
            }
            const bgWidth = textWidth + (bgPadding * 2);
            const bgHeight = textHeight + (bgPadding * 2);
            
            this.ctxOverlay.fillRect(bgX, bgY, bgWidth, bgHeight);
            
            // Draw text in darker color for contrast
            this.ctxOverlay.fillStyle = '#1a1a1a';
            this.ctxOverlay.fillText(level.toString(), textX, textY);
        } else if (isWall) {
            // Wall: Dark background with white text (only at 0,0)
            this.ctxOverlay.fillStyle = 'rgba(0, 0, 0, 0.6)';
            this.ctxOverlay.font = `bold ${fontSize}px Arial, sans-serif`;
            this.ctxOverlay.textAlign = textAlign;
            this.ctxOverlay.textBaseline = textBaseline;
            
            // Measure text to create background
            const metrics = this.ctxOverlay.measureText(level.toString());
            const textWidth = metrics.width;
            const textHeight = fontSize;
            const bgPadding = 2;
            
            // Draw background
            const bgX = textX - bgPadding;
            const bgY = textY - bgPadding;
            const bgWidth = textWidth + (bgPadding * 2);
            const bgHeight = textHeight + (bgPadding * 2);
            
            this.ctxOverlay.fillRect(bgX, bgY, bgWidth, bgHeight);
            
            // Draw text
            this.ctxOverlay.fillStyle = '#ffffff';
            this.ctxOverlay.fillText(level.toString(), textX, textY);
        } else {
            // Regular buildings: Dark background with white text
            this.ctxOverlay.fillStyle = 'rgba(0, 0, 0, 0.6)';
            this.ctxOverlay.font = `bold ${fontSize}px Arial, sans-serif`;
            this.ctxOverlay.textAlign = textAlign;
            this.ctxOverlay.textBaseline = textBaseline;
            
            // Measure text to create background
            const metrics = this.ctxOverlay.measureText(level.toString());
            const textWidth = metrics.width;
            const textHeight = fontSize;
            const bgPadding = 2;
            
            // Draw background
            const bgX = textX - textWidth - bgPadding;
            const bgY = textY - textHeight - bgPadding;
            const bgWidth = textWidth + (bgPadding * 2);
            const bgHeight = textHeight + (bgPadding * 2);
            
            this.ctxOverlay.fillRect(bgX, bgY, bgWidth, bgHeight);
            
            // Draw text
            this.ctxOverlay.fillStyle = '#ffffff';
            this.ctxOverlay.fillText(level.toString(), textX, textY);
        }
        
        this.ctxOverlay.restore();
    }
    
    /**
     * Draw tile synchronously (uses cached images only)
     */
    drawTileSync(i, j, tileType) {
        const pos = this.gridToPixel(i, j);
        
        // Skip if outside bounds
        if (pos.x < -this.tileWidth || pos.x > this.canvasWidth ||
            pos.y < -this.tileHeight || pos.y > this.canvasHeight) {
            return;
        }
        
        // NOTE: Gridlines are now drawn separately in renderSync() for proper layering
        // This ensures wheat fields appear above gridlines
        
        // Draw wheat field first (if present) so buildings appear on top
        if (this.gameEngine && this.gameEngine.hasWheatField(i, j)) {
            this.drawWheatField(pos.x, pos.y);
        }
        
        // Draw tile content (building/resource image) - only if already loaded
        if (shouldRenderTile(tileType) && this.assetLoader) {
            try {
                const imageKey = getTileImageKey(tileType);
                if (imageKey) {
                    const image = this.assetLoader.images[imageKey];
                    if (image && image.complete && image.naturalWidth > 0) {
                        // Scale 64x64 image to fit within tile (38x27), maintaining aspect ratio
                        const imageSize = 64;
                        const scale = Math.min(this.tileWidth / imageSize, this.tileHeight / imageSize);
                        const scaledWidth = imageSize * scale;
                        const scaledHeight = imageSize * scale;
                        const offsetX = (this.tileWidth - scaledWidth) / 2;
                        const offsetY = (this.tileHeight - scaledHeight) / 2;
                        this.ctx.drawImage(image, pos.x + offsetX, pos.y + offsetY, scaledWidth, scaledHeight);
                    }
                }
            } catch (error) {
                // Image not loaded
            }
        }
        
        // Draw lock indicator if locked
        if (this.gameEngine && this.gameEngine.isLocked(i, j)) {
            const lockImage = this.assetLoader?.images['locked'];
            if (lockImage && lockImage.complete) {
                this.ctx.drawImage(lockImage, pos.x + this.tileWidth - 16, pos.y + 6, 16, 16);
            } else {
                // Fallback: red overlay
                this.ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
                this.ctx.fillRect(pos.x + this.tileWidth - 16, pos.y + 6, 16, 16);
            }
        }
        
        // Draw hover highlight
        if (this.hoverTile && this.hoverTile.i === i && this.hoverTile.j === j) {
            this.ctx.strokeStyle = '#00ff00';
            this.ctx.lineWidth = 2;
            this.ctx.strokeRect(pos.x, pos.y, this.tileWidth, this.tileHeight);
        }
        
        // Draw cursor overlay based on cursor mode
        if (this.shouldShowCursor(i, j)) {
            this.drawCursor(i, j);
        }
        
        // Draw heat map overlay
        if (this.showHeatMap) {
            this.drawHeatMap(i, j, tileType);
        }
        
        // Note: Wheat fields are drawn in a separate pass after all tiles
        // to ensure they appear above gridlines
    }
    
    /**
     * Draw heat map overlay for a tile on a specific context
     * @param {number} i - Row position
     * @param {number} j - Column position
     * @param {number} tileType - Tile type constant
     * @param {CanvasRenderingContext2D} ctx - The context to draw on (defaults to overlay layer)
     */
    drawHeatMapOnContext(i, j, tileType, ctx = null) {
        if (!this.gameEngine) return;
        
        const targetCtx = ctx || this.ctxOverlay || this.ctx;
        if (!targetCtx) return;
        
        const pos = this.gridToPixel(i, j);
        const heatData = this.gameEngine.getHeatMapValue(i, j);
        const { val, touches } = heatData;
        
        // Compute color and alpha
        const color = this.computeHeatMapColor(tileType, val, touches);
        const alpha = this.computeHeatMapAlpha(tileType, val, touches);
        const text = this.computeHeatMapText(val, touches);
        
        // Draw colored background
        targetCtx.fillStyle = color;
        targetCtx.globalAlpha = alpha;
        targetCtx.fillRect(pos.x, pos.y, this.tileWidth, this.tileHeight);
        targetCtx.globalAlpha = 1.0;
        
        // Draw text
        if (text) {
            targetCtx.fillStyle = '#000000';
            targetCtx.font = '11px monospace';
            targetCtx.textAlign = 'center';
            targetCtx.textBaseline = 'middle';
            targetCtx.fillText(text, pos.x + this.tileWidth / 2, pos.y + this.tileHeight / 2);
        }
    }
    
    /**
     * Draw heat map overlay for a tile (uses default context)
     */
    drawHeatMap(i, j, tileType) {
        this.drawHeatMapOnContext(i, j, tileType, this.ctx);
    }
    
    /**
     * Compute heat map color (fade from red to green)
     */
    computeHeatMapColor(tileType, val, touches) {
        // Gray for resources, open, water
        if (isRealResource(tileType) || tileType === OPEN || tileType === WATER || tileType === DEEP_WATER) {
            return '#999999';
        }
        
        // Calculate touches ratio
        const isMilitaryTile = isMilitary(tileType);
        const maxTouches = isMilitaryTile ? 6.0 :
                          tileType === CELLARIUM ? 3.0 :
                          tileType === CACHE ? 4.0 :
                          tileType === BARRACKS ? 6.0 :
                          (tileType & 0xf) === 0x2 ? 4.0 : // Refiner
                          1000;
        
        const touchesRatio = touches / maxTouches;
        
        // Calculate resource value ratio
        const maxValue = tileType === MANOR ? 600.0 :
                        tileType === WHARF ? 600.0 :
                        tileType === BAZAAR ? 400.0 :
                        tileType === FARM ? 2100.0 :
                        tileType === MILL ? 2700.0 :
                        (tileType & 0xf) === 0x2 ? 1500.0 : // Refiner
                        1300.0; // Regular producer
        
        const resourceValueRatio = val / maxValue;
        
        // Combined ratio (clamped 0-1)
        const ratio = Math.max(0, Math.min(1.0, touchesRatio + resourceValueRatio));
        
        // Fade from red (0xff4444) to green (0x44ff44)
        return this.fadeHex(0xff4444, 0x44ff44, ratio);
    }
    
    /**
     * Fade between two hex colors
     */
    fadeHex(hex1, hex2, ratio) {
        const r1 = (hex1 >> 16) & 0xff;
        const g1 = (hex1 >> 8) & 0xff;
        const b1 = hex1 & 0xff;
        
        const r2 = (hex2 >> 16) & 0xff;
        const g2 = (hex2 >> 8) & 0xff;
        const b2 = hex2 & 0xff;
        
        const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
        const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
        const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
        
        return `rgb(${r}, ${g}, ${b})`;
    }
    
    /**
     * Compute heat map alpha
     */
    computeHeatMapAlpha(tileType, val, touches) {
        if (val === 0 && touches === 0) {
            return 0.3;
        }
        if (isResource(tileType)) {
            return 0.6;
        }
        return 0.9;
    }
    
    /**
     * Compute heat map text
     */
    computeHeatMapText(val, touches) {
        if (touches) {
            if (val) {
                return `x${touches}, ${Math.round(val)}`;
            } else {
                return `x${touches}`;
            }
        } else if (val) {
            return `${Math.round(val)}`;
        }
        return '';
    }
    setCursorMode(mode) {
        this.cursorMode = mode;
        this.renderSync(); // Use renderSync for multi-layer rendering
    }
    
    /**
     * Set gridlines visibility
     */
    setShowGridlines(show) {
        this.showGridlines = show;
        this.renderSync(); // Use renderSync to ensure multi-layer rendering with wheat fields
    }
    
    /**
     * Set building levels visibility
     */
    setShowBuildingLevels(show) {
        this.showBuildingLevels = show;
        this.renderSync(); // Use renderSync for multi-layer rendering
    }
    
    /**
     * Set terrain mode (land/water)
     */
    setWater(water) {
        this.water = water;
        this.renderSync(); // Use renderSync for multi-layer rendering
    }
    
    /**
     * Set heat map visibility
     */
    setShowHeatMap(show) {
        this.showHeatMap = show;
        this.renderSync(); // Use renderSync for multi-layer rendering
    }
    
    
    /**
     * Update from game engine state
     */
    update() {
        // Sync water property with gameEngine before rendering
        if (this.gameEngine) {
            this.water = this.gameEngine.water;
        }
        this.renderSync();
        // Update scale after rendering to ensure canvas fits container
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
            this.updateScale();
        }, 0);
    }
}

